import {
  BookOpenText as BookOpenTextIcon,
  Factory as FactoryIcon,
  MessageSquarePlus as FeedbackIcon,
  Layers as CirclesThreePlusIcon,
  LayoutDashboard as DashboardsIcon,
  History as HistoryIcon,
  ListChecks as TasksIcon,
  MessagesSquare,
  Network as GraphIcon,
  NotebookText as DocsIcon,
  OctagonAlert as ProblemsIcon,
  Settings as GearSixIcon,
  SlidersHorizontal as SlidersIcon,
  Tag as TagIcon,
  UsersRound,
  Activity,
  type LucideIcon
} from 'lucide-react'
import { useEffect, useRef } from 'react'
import { Link, Navigate, Outlet, useLocation } from 'react-router-dom'
import { ARTIFACTS_ROOT, PLANT_HREF } from '@atbash-labs/plantview-links'
import { cn } from '../../lib/utils'
import {
  customAgentsApi,
  graphApi,
  initUsageAnalytics,
  isDemoMode,
  notificationsApi,
  plantViewerApi,
  store,
  useAppDispatch,
  useAppSelector,
  useGetAuthzMeQuery,
  viewAsUserIdSelector
} from '@axilon-apps/domains-ontology-ui-state'
import { isCurrentUserAdminSelector } from '@axilon-apps/domains-identity/state'
import { RouteTelemetry } from '../RouteTelemetry/RouteTelemetry'
import { OPS_FLAG_KEYS, flagValue } from '@atbash-labs/ontology-flags'
import { useDebugMode } from '../../lib/debug-mode'
import { useFlagSnapshot } from '../flags/FlagSnapshotProvider'
import { isCoreView, useOperatorView } from '../../lib/operator-view-flag'
import { type WorkPage, workPagesForAccess, WORK_PAGE_READ_PERMISSIONS } from '../../lib/work-page'
import { Button } from '../ui/button'
import GlobalQuerySidebar from '../GlobalQuerySidebar/GlobalQuerySidebar'
import { chatNavPath, isChatPath } from '../Chat/chat-route'
import { rememberChatReturnPath } from '../Chat/chat-handoff'
import { NotificationsBell } from '../Notifications/NotificationsBell'
import { BrowserNotificationsWatcher } from '../Notifications/BrowserNotificationsWatcher'
import { FeedbackDialog } from '../Feedback/FeedbackDialog'
import { BranchCommitButton } from '../Branches/BranchCommitButton'
import { BranchMergeButton } from '../Branches/BranchMergeButton'
import { BranchSelector } from './BranchSelector'
import { OverlayScrollbar } from './OverlayScrollbar'
import { GraphChangePoller } from './GraphChangePoller'
import { EventStreamProvider } from './EventStreamProvider'
import { ViewAsBanner, ViewAsControl } from './ViewAsControl'
import { IngestWizardCoordinator } from '../../pages/IngestPage/wizard/IngestWizardCoordinator'
import { TAGS_PAGE_ROLES } from '../../pages/TagsPage/tags-page-access'
import prophetLogoLight from '../../assets/sm_profile_light.png'
import prophetLogoDark from '../../assets/sm_profile_dark.png'

interface NavigationGroup {
  key: string
  title: string
  items: NavigationItem[]
}

interface NavigationItem {
  key: string
  title: string
  href: string
  icon: LucideIcon
  requiredPermissions?: readonly string[]
  requiredRoles?: readonly string[]
}

/**
 * The proxy owns the effective permission set, including all role inheritance.
 * Navigation must use that set rather than reconstructing the policy in the
 * browser. Each entry names the permission(s) required for its initial page
 * load; write controls inside the page remain independently guarded.
 */
export function canAccessNavigation(
  permissions: readonly string[],
  roles: readonly string[],
  item: Pick<NavigationItem, 'requiredPermissions' | 'requiredRoles'>
): boolean {
  return (
    item.requiredPermissions?.every((permission) => permissions.includes(permission)) !== false &&
    item.requiredRoles?.some((role) => roles.includes(role)) !== false
  )
}

const baseNavigation: NavigationGroup[] = [
  {
    key: 'ontology',
    title: 'Axilon Prophet',
    items: [
      {
        key: 'chat',
        title: 'Chat',
        href: '/chat',
        icon: MessagesSquare,
        requiredPermissions: ['chat/use']
      },
      {
        key: 'problems',
        title: 'Problems',
        href: '/problems',
        icon: ProblemsIcon,
        requiredPermissions: [WORK_PAGE_READ_PERMISSIONS.problems]
      },
      {
        key: 'tasks',
        title: 'Tasks',
        href: '/tasks',
        icon: TasksIcon,
        requiredPermissions: [WORK_PAGE_READ_PERMISSIONS.tasks]
      },
      {
        key: 'dashboards',
        title: 'Dashboards',
        href: '/dashboards',
        icon: DashboardsIcon,
        requiredPermissions: ['dashboard/read']
      },
      {
        key: 'tags',
        title: 'Tags',
        href: '/tags',
        icon: TagIcon,
        requiredPermissions: ['tag/inventory-read'],
        requiredRoles: TAGS_PAGE_ROLES
      },
      {
        key: 'docs',
        title: 'Documents',
        href: '/documents',
        icon: DocsIcon,
        requiredPermissions: ['docs/read']
      },
      {
        key: 'graph',
        title: 'Graph',
        href: '/graph',
        icon: GraphIcon,
        requiredPermissions: ['debug/access', 'graph/read']
      },
      {
        key: 'plant',
        title: 'Plant',
        href: PLANT_HREF.root,
        icon: FactoryIcon,
        requiredPermissions: ['graph/read']
      },
      {
        key: 'configure',
        title: 'Configure',
        href: '/configure',
        icon: SlidersIcon,
        requiredPermissions: ['agents:custom:config/read', 'notification-rule/read'],
        requiredRoles: ['global_admin']
      },
      {
        key: 'users',
        title: 'Users',
        href: '/users',
        icon: UsersRound,
        requiredRoles: ['global_admin', 'admin']
      },
      {
        key: 'feedback',
        title: 'Feedback',
        href: '/feedback',
        icon: FeedbackIcon,
        requiredRoles: ['global_admin']
      },
      {
        key: 'usage',
        title: 'Usage',
        href: '/usage',
        icon: Activity,
        requiredRoles: ['global_admin', 'admin']
      },
      {
        key: 'ingest',
        title: 'Ingest',
        href: '/ingest',
        icon: CirclesThreePlusIcon,
        requiredPermissions: ['ingest/read']
      }
    ]
  }
]

// Nav items only shown while debug mode is on: Graph and Plant in the primary
// nav, Guide in the footer. The Graph and Guide routes are also wrapped in
// <DebugRoute> so direct URLs 404 for end users; Plant stays reachable because
// chat answers and other pages link into it. Configure is gated by the
// global_admin role instead (nav item + <GlobalAdminRoute>).
const DEBUG_ONLY_NAV_KEYS = new Set(['graph', 'plant', 'guide'])

// Problems and Tasks share one sidebar slot; the role picks which one shows.
const WORK_PAGE_NAV_KEYS = new Set<string>(['problems', 'tasks'] satisfies WorkPage[])

// The trimmed Core edition exposes only these primary nav sections; everything
// else (Graph, Plant, Ingest, Tags) is hidden.
const CORE_NAV_KEYS = new Set([
  'chat',
  'problems',
  'tasks',
  'dashboards',
  'docs',
  'configure',
  'feedback'
])

// Route prefixes that Core hides entirely — a direct URL (deep link, bookmark,
// stale redirect) to any of these bounces to /dashboards.
const CORE_BLOCKED_PATH_PREFIXES = ['/graph', '/plant', '/ingest', '/guide', '/tags', '/branches']

function isCoreBlockedPath(pathname: string): boolean {
  return CORE_BLOCKED_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  )
}

function filterNavigation(
  groups: NavigationGroup[],
  debugEnabled: boolean,
  coreView: boolean,
  permissions: readonly string[],
  roles: readonly string[]
): NavigationGroup[] {
  const workPages: readonly string[] = workPagesForAccess(roles, permissions, debugEnabled)
  return groups.map((group) => ({
    ...group,
    items: group.items.filter((item) => {
      if (coreView && !CORE_NAV_KEYS.has(item.key)) return false
      if (!debugEnabled && DEBUG_ONLY_NAV_KEYS.has(item.key)) return false
      if (WORK_PAGE_NAV_KEYS.has(item.key) && !workPages.includes(item.key)) return false
      return canAccessNavigation(permissions, roles, item)
    })
  }))
}

// Pinned to the bottom of the sidebar below the Feedback button, rendered
// top-to-bottom in this order. Guide is debug-only and sits directly above
// Settings. Releases are global-admin only; Settings stays available to every
// ontology user (notification links also deep-link into it).
const footerNavItems: NavigationItem[] = [
  {
    key: 'releases',
    title: 'Releases',
    href: '/releases',
    icon: HistoryIcon,
    requiredPermissions: [],
    requiredRoles: ['global_admin']
  },
  {
    key: 'guide',
    title: 'Guide',
    href: '/guide',
    icon: BookOpenTextIcon,
    requiredPermissions: []
  },
  {
    key: 'settings',
    title: 'Settings',
    href: '/settings',
    icon: GearSixIcon
  }
]

const TopBar = () => {
  const demo = isDemoMode()
  const coreView = isCoreView(useOperatorView())
  const isGlobalAdmin = useAppSelector(isCurrentUserAdminSelector)
  return (
    <div className="sticky top-0 z-10 shrink-0 bg-background">
      <div className="flex items-center gap-3 border-b border-border px-4 py-2 md:pl-6 md:pr-3">
        {/* Core hides all branching chrome (selector + commit/merge). */}
        {!coreView && (
          <>
            <BranchSelector />
            {!demo && (
              <>
                <BranchCommitButton />
                <BranchMergeButton />
              </>
            )}
          </>
        )}
        <div className="flex-1" />
        {isGlobalAdmin && <ViewAsControl />}
        <NotificationsBell />
      </div>
      <ViewAsBanner />
      <KillSwitchBanner />
    </div>
  )
}

function KillSwitchBanner() {
  const { snapshot, stale } = useFlagSnapshot()
  const paused = OPS_FLAG_KEYS.filter((key) => !flagValue(snapshot.flags, key))
  if (paused.length === 0) return null
  return (
    <div className="flex min-h-9 items-center justify-center gap-2 border-b border-amber-300 bg-amber-50 px-4 text-xs text-amber-950 dark:border-amber-700 dark:bg-amber-950/35 dark:text-amber-100">
      <span>
        Operator pause in effect: {paused.join(', ')}
        {stale ? ' (a newer snapshot will apply on the next navigation)' : ''}
      </span>
    </div>
  )
}

const AppLayout = () => {
  const location = useLocation()
  const debugEnabled = useDebugMode()
  const coreView = isCoreView(useOperatorView())
  const { data: authz } = useGetAuthzMeQuery()
  const viewAsUserId = useAppSelector(viewAsUserIdSelector)
  const activeChatTabId = useAppSelector((s) => s.agentOrchestrator.activeTabId)
  const chatHref = chatNavPath(location.pathname, activeChatTabId)
  const navigation = filterNavigation(
    baseNavigation,
    debugEnabled,
    coreView,
    authz?.permissions ?? [],
    authz?.roles ?? []
  ).map((group) => ({
    ...group,
    items: group.items.map((item) => (item.key === 'chat' ? { ...item, href: chatHref } : item))
  }))
  // Core hides the Guide. Everywhere else it follows debug mode, same as Graph.
  // Releases and Settings stay reachable.
  const footerItems = footerNavItems.filter(
    (item) =>
      (debugEnabled || !DEBUG_ONLY_NAV_KEYS.has(item.key)) &&
      (!coreView || item.key !== 'guide') &&
      canAccessNavigation(authz?.permissions ?? [], authz?.roles ?? [], item)
  )
  const demo = isDemoMode()
  const mainRef = useRef<HTMLElement>(null)
  const dispatch = useAppDispatch()
  const previousViewAsUserId = useRef<string | undefined>(undefined)
  const chatRoute = isChatPath(location.pathname)
  const artifactRoute = location.pathname.startsWith(`${ARTIFACTS_ROOT}/`)

  useEffect(() => {
    rememberChatReturnPath(location.pathname, location.search)
  }, [location.pathname, location.search])

  // Every RTK Query cache key is scoped by endpoint arguments rather than the
  // view-as header. Drop existing data when the effective identity changes so
  // an administrator's cached rows can never be rendered as the preview user.
  useEffect(() => {
    if (previousViewAsUserId.current === viewAsUserId) return
    previousViewAsUserId.current = viewAsUserId
    for (const key of Object.keys(store.getState())) {
      // identityApi owns AuthenticationGuard's boot-time current-user query;
      // resetting it strands the whole app on its loading screen. Authz is
      // deliberately kept too so the selection confirmation remains valid.
      if (key.endsWith('Api') && key !== 'authzApi' && key !== 'identityApi') {
        dispatch({ type: `${key}/resetApiState` })
      }
    }
  }, [dispatch, viewAsUserId])

  // Product usage analytics: session heartbeats + unload flush (page_view
  // rides RouteTelemetry on each navigation).
  useEffect(() => {
    initUsageAnalytics()
  }, [])

  // Prophet demo: when tour Back locks an ingest slice, refetch plant/graph
  // so the UI shows the rolled-back ontology instead of RTK-cached unlocked data.
  useEffect(() => {
    if (!demo || typeof window === 'undefined') return
    const onDemoStateChange = () => {
      dispatch(graphApi.util.invalidateTags(['Graph']))
      dispatch(plantViewerApi.util.invalidateTags(['Plant']))
    }
    window.addEventListener('prophet-demo-state-change', onDemoStateChange)
    return () => window.removeEventListener('prophet-demo-state-change', onDemoStateChange)
  }, [demo, dispatch])

  // Prophet demo: the "Simulate" button seeds a finding + notification via the
  // fixture theater once its hardcoded run completes. Refresh the custom-agent
  // surfaces + notification bell so the new row shows without a reload.
  useEffect(() => {
    if (!demo || typeof window === 'undefined') return
    const onInsightAdded = () => {
      dispatch(notificationsApi.util.invalidateTags(['Notification']))
      dispatch(
        customAgentsApi.util.invalidateTags([
          'CustomAgentConfig',
          'CustomAgentRun',
          'CustomAgentTask'
        ])
      )
    }
    window.addEventListener('prophet-demo-insight-added', onInsightAdded)
    return () => window.removeEventListener('prophet-demo-insight-added', onInsightAdded)
  }, [demo, dispatch])

  return (
    <div className="grid h-screen grid-cols-1 overflow-hidden bg-background text-foreground md:grid-cols-[auto_minmax(0,1fr)] xl:grid-cols-[auto_minmax(0,1fr)_auto]">
      <aside
        aria-label="Ontology navigation"
        className={cn(
          'z-20 flex shrink-0 flex-col gap-3 overflow-hidden bg-background p-3',
          'md:sticky md:top-0 md:h-screen md:w-39 md:border-r md:pr-4'
        )}
      >
        <div className="flex items-center gap-3 rounded-lg px-2">
          <div className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-lg md:-ml-[2px]">
            <img
              src={prophetLogoLight}
              alt="Axilon Prophet"
              className="block size-full object-cover dark:hidden"
            />
            <img
              src={prophetLogoDark}
              alt="Axilon Prophet"
              className="hidden size-full object-cover dark:block"
            />
          </div>
          <div className="min-w-0 overflow-hidden whitespace-nowrap">
            <h1 className="m-0 truncate text-sm font-semibold text-ink">Axilon</h1>
            <p className="m-0 truncate text-xs text-ink-3">Prophet</p>
          </div>
        </div>

        <nav className="flex min-w-0 flex-1 flex-col gap-4 pt-3.25" aria-label="Ontology sections">
          {navigation.map((group) => (
            <div key={group.key} className="grid gap-1">
              {group.items.map((item) => (
                <SidebarNavItem key={item.key} item={item} pathname={location.pathname} />
              ))}
            </div>
          ))}
        </nav>

        <div className="grid gap-1">
          <FeedbackDialog />
          {footerItems.map((item) => (
            <SidebarNavItem
              key={item.key}
              item={item}
              pathname={location.pathname}
              disabled={demo && item.key === 'settings'}
            />
          ))}
        </div>
      </aside>

      {/* Reserve a 48 px gutter on the right for the collapsed chat rail
          below `xl`, where the rail floats as a fixed overlay. At `xl+`
          the rail/panel is part of the grid and consumes its own column,
          so no padding is needed.

          `<main>` owns its own vertical scroll so the scrollbar lives
          inside the main column (i.e. to the left of the docked chat
          sidebar at xl+) rather than as a document-level scrollbar at
          the right edge of the viewport. Its native bar is hidden
          (`overlay-scroll-host`) and replaced by a fading overlay thumb
          that reserves no horizontal width; the `relative` wrapper is the
          thumb's positioning context so it tracks the viewport. */}
      <div className="relative h-screen min-w-0">
        <main
          ref={mainRef}
          className={cn(
            'overlay-scroll-host flex h-screen min-w-0 flex-col overflow-y-auto',
            !chatRoute && 'pr-12 xl:pr-0'
          )}
        >
          <TopBar />
          {/* Key by top-level section (`plant`, `ingest`, …) so nested
              drill-downs keep layout state (e.g. Plant session paint filter).
              Full pathname remounts wiped that state on every navigation. */}
          {/* Chat and the artifact page are near-full-bleed surfaces: they
              own their own header rule and gutters, so they take only a
              small breath below the top bar instead of the full page
              padding. This keeps the artifact title on the chat title's line. */}
          <div
            key={location.pathname.split('/').filter(Boolean)[0] ?? ''}
            className={cn(
              'min-w-0 flex-1 duration-200 animate-in fade-in',
              chatRoute || artifactRoute ? 'pt-3' : 'p-4 md:p-6'
            )}
          >
            {coreView && isCoreBlockedPath(location.pathname) ? (
              <Navigate to="/dashboards" replace />
            ) : (
              <Outlet />
            )}
          </div>
        </main>
        <OverlayScrollbar targetRef={mainRef} />
      </div>
      {authz?.permissions.includes('ingest/read') ? <IngestWizardCoordinator /> : null}
      <GraphChangePoller />
      <EventStreamProvider />
      <RouteTelemetry />
      <BrowserNotificationsWatcher />
      {chatRoute ? null : <GlobalQuerySidebar />}
    </div>
  )
}

const SidebarNavItem = ({
  item,
  pathname,
  disabled = false
}: {
  item: NavigationItem
  pathname: string
  disabled?: boolean
}) => {
  const Icon = item.icon
  const active =
    item.key === 'chat'
      ? isChatPath(pathname)
      : pathname === item.href || pathname.startsWith(`${item.href}/`)
  const content = (
    <>
      <span
        className={cn(
          'flex size-9 shrink-0 items-center justify-center rounded-md transition-colors md:-ml-[2px]'
        )}
      >
        {/* Lucide has no fill/regular weights — approximate the
            active-fill emphasis with a thicker stroke. */}
        <Icon className="size-4.5" strokeWidth={active ? 2.5 : 1.75} />
      </span>
      <span className="min-w-0 overflow-hidden text-11 font-medium tracking-[0.08em] whitespace-nowrap uppercase">
        {item.title}
      </span>
    </>
  )

  return (
    <Button
      asChild={!disabled}
      variant="ghost"
      disabled={disabled}
      title={disabled ? 'Not available in this demo' : undefined}
      className={cn(
        'h-10 w-full justify-start gap-1.5 px-2 duration-200',
        'hover:bg-brand-soft/50 hover:text-ink dark:hover:bg-brand/15 dark:hover:text-ink',
        disabled && 'cursor-not-allowed opacity-45 hover:bg-transparent',
        active && 'bg-brand-soft text-brand hover:bg-brand-soft hover:text-brand'
      )}
    >
      {disabled ? (
        <span aria-label={item.title}>{content}</span>
      ) : (
        <Link to={item.href}>{content}</Link>
      )}
    </Button>
  )
}

export default AppLayout
